import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Details, Section, StateView, useAuth, useToast } from "@ozpanel/ui";
import { useMailboxApi } from "./api";

// Opening webmail, which is entirely a question of WHICH ORIGIN this page is on.
//
// The SSO endpoint mints a one-time token and returns a ROOT-RELATIVE url — "/roundcube/?ozsso=…".
// That is correct for v1, which is served from the tenant's own domain, where Caddy mounts
// webmail. This console is served from the PANEL origin (:9600), where nothing is mounted: the
// iframe asked the panel for /roundcube/ and got the API's own 404 envelope,
// {"ok":false,"error":"route not found"}, rendered inside the frame.
//
// The mailbox app's vite config already recorded this as G49 — "once it is deployed, this must be
// served on webmail.<domain> itself, same-origin with SOGo/Roundcube" — and it was deployed to the
// panel origin anyway. Until the hosting decision is made, the console has to cross the origin
// itself, and it can: the token IS the credential (/sso/redeem is unauthenticated), so a
// top-level navigation to the right host works where an iframe never could.
//
// Two rules taken from v1, both of which cost something when ignored:
//
//   * SOGo opens in its OWN TAB. It authenticates by OpenID Connect, which does top-level
//     redirects; inside a frame those fight the shell and land back on this page. Roundcube's SSO
//     is a plugin with no redirects, so it can be framed.
//   * The token is minted AT CLICK TIME. It expires in 90 seconds, so one minted on page load is
//     usually dead by the time anyone presses anything.

interface Client {
  name: string;
  title: string;
  description?: string;
  calendars?: boolean;
}

// There is no webmailOrigins() any more.
//
// It built "webmail.<domain>" and "<domain>" in the BROWSER and rendered a button for each,
// under copy that told the user to try the other one if the first did not work. That is a guess
// presented as a choice: which of the two a domain actually uses is a fact the server holds in
// domain_support_services, and the SSO mint already answers with absolute URLs derived from it.
// The same guess, in the same component, is what used to send every path-exposed domain to a URL
// that did not exist.

export function WebmailView({ client }: { client: string }) {
  const api = useMailboxApi();
  const { who } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [framed, setFramed] = React.useState<string | null>(null);

  const clients = useQuery({
    queryKey: ["webmail-clients"],
    queryFn: () => api.webmailClients(),
    retry: false,
  });

  const list: Client[] = ((clients.data as { clients?: Client[] } | undefined)?.clients ?? []) as Client[];

  // Framing is possible only when this console is served from the same origin as webmail —
  // which is the case on webmail.<domain>, where the mailbox console now lives, and never on the
  // panel host: the clients send X-Frame-Options: SAMEORIGIN, so a cross-origin frame renders
  // nothing at all.
  const here = window.location.origin;

  const open = async (name: string) => {
    try {
      // Cross-origin: hand the session across so the browser lands in the WRAPPER — the ozpanel
      // bar with webmail inside it — rather than on the bare client. Without this the same
      // mailbox gets a different product depending on whether its owner arrived by password or
      // from the panel, which is not a difference a user should be able to feel.
      // SOGo INCLUDED, and for a stronger reason than Roundcube. Its sign-on is a signed cookie
      // set on the RESPONSE, so asking the panel for it puts the cookie on the panel's host —
      // where SOGo's forward_auth gate never looks. It could not work cross-origin at all. The
      // hand-off moves the session to the webmail host first, and the mint then happens there,
      // where the cookie belongs.
      {
        try {
          const h = await api.webmailHandoff(name);
          const url = (h.urls ?? [])[0] ?? h.url;
          if (url && !url.startsWith(here + "/")) {
            window.open(url, "_blank", "noopener");
            return;
          }
        } catch {
          // Fall through to the direct sign-in below: losing the bar beats losing webmail.
        }
      }
      const sso = await api.mintSSO(name as never);
      // Absolute URLs come from the server, which reads the domain's actual exposure. Anything
      // this component built itself was a guess about where webmail is mounted, and it was wrong
      // for every domain exposed by path.
      // The server's own answer, in its own order. Where webmail is mounted is not something
      // this component may decide.
      // ABSOLUTE ONLY. The mint also returns a relative url ("/SOGo/"), and a browser resolves
      // that against the origin it is ON — the panel — which is a 404. Refusing it here means a
      // server that cannot say where webmail lives produces a clear error instead of a dead tab.
      const abs = (sso.urls ?? []).filter((u) => u.startsWith("https://"));
      const url = abs[0];
      if (!url) {
        notify({
          kind: "error",
          title: "Could not open webmail",
          body: "The server could not say where your webmail is served. Ask your administrator to set it up.",
        });
        return;
      }

      if (name === "sogo") {
        // Its own tab, always: OIDC's top-level redirects cannot happen inside a frame. Reached
        // only when we are ALREADY on the webmail host, so the cookie the mint just set is on
        // the right host.
        window.open(url, "_blank", "noopener");
        return;
      }
      // Frame it when webmail is served from this very origin; otherwise a new tab, because the
      // browser will not render a cross-origin frame of a SAMEORIGIN page.
      if (url.startsWith(here + "/")) {
        setFramed(url);
        return;
      }
      window.open(url, "_blank", "noopener");
    } catch (e) {
      notify({ kind: "error", title: "Could not open webmail", body: e instanceof Error ? e.message : String(e) });
    }
  };

  // Arriving AT the framed entry opens it, rather than showing a button that says "open".
  // /app is the hub's webmail path and the hand-off's redirect target, and ?client= says which
  // one was asked for — dropping that is what made choosing SOGo land on Roundcube.
  //
  // Once only, and only when the client is actually served from this origin: on the panel host a
  // frame is refused (X-Frame-Options: SAMEORIGIN), so auto-opening there would spawn a tab the
  // user did not ask for on every page load.
  const autoOpened = React.useRef(false);
  React.useEffect(() => {
    if (autoOpened.current || framed) return;
    const p = new URLSearchParams(window.location.search);
    const wanted = (p.get("client") ?? "").toLowerCase();
    const onWebmailHost = !here.includes("://" + (who?.email?.split("@")[1] ?? "\u0000")) || here.includes("webmail.");
    if (!onWebmailHost) return;
    const atApp = window.location.pathname.replace(/\/+$/, "").endsWith("/app");
    if (!atApp && !wanted) return;
    // WAIT for the client list. This ran on the first render, when the list is still empty, so
    // `list.some(name === wanted)` was false and the requested client fell back to the default —
    // /app?client=sogo opened Roundcube. The guard is on the QUERY, not on list.length, because
    // an empty list after loading is a real answer (no webmail installed) and must not spin.
    if (clients.isLoading) return;
    autoOpened.current = true;
    const known = list.some((c) => c.name === wanted);
    if (wanted && !known) {
      // Asked for something this node does not serve. Say so rather than silently opening
      // something else — substituting a different client is how this bug hid.
      notify({
        kind: "error",
        title: `${wanted} is not available here`,
        body: "This server does not have that webmail client installed.",
      });
      return;
    }
    void open(wanted || client);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients.isLoading, list.length, client]);

  if (framed) {
    return (
      // No strip above the frame. It carried a "Webmail" label and a Back button, and cost a
      // row of height on every screen to duplicate navigation the console already has directly
      // above it — the tab bar is still there, and leaving the frame is one tap on it. On a
      // phone that row was a meaningful share of what is left for the mailbox itself.
      // Fills the space between the header and (in the installed app) the tab bar, edge to edge:
      // the page's own padding is taken back so the mailbox gets every pixel of a phone.
      <div className="-mx-3 -my-4 h-[calc(100dvh-3.5rem-env(safe-area-inset-top))] sm:-mx-5 sm:-my-6 [.has-bottom-tabs_&]:h-[calc(100dvh-3.5rem-76px-env(safe-area-inset-top)-env(safe-area-inset-bottom))]">
        <iframe title="Webmail" src={framed} className="h-full w-full border-0" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Section title="Open your webmail">
        <p className="text-[13px] text-muted">Opens without asking for your password again.</p>
        <Details summary="How that works" className="mt-1">
          <p>Each button creates a one-time sign-in link that lasts ninety seconds.</p>
        </Details>

        {!here.includes("webmail.") && (
          // Says where the full experience is instead of only apologising for this page. On the
          // panel host webmail can only ever open in a new tab: the clients send
          // X-Frame-Options: SAMEORIGIN, so no cross-origin page may frame them — which is why
          // the mailbox console is also served from webmail.<domain>, where it CAN.
          <div className="mt-3 rounded-lg border border-line bg-bg2 px-3 py-2.5 text-[13px] leading-relaxed text-muted">
            From this address webmail opens in a new tab. At your webmail address it opens right here.
          </div>
        )}

        <StateView loading={clients.isLoading} error={clients.error} errorContext="the webmail options">
          <div className="mt-3 divide-y divide-line rounded-lg border border-line">
            {list.length === 0 && (
              <div className="px-4 py-6 text-center text-[13px] text-muted">
                No webmail is installed on this server.
              </div>
            )}
            {list.map((c) => (
              <div key={c.name} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[14px] font-medium">
                    {c.title?.replace(/ (webmail|groupware)$/i, "") || c.name}
                    {c.name === client && <span className="ml-2 text-[12px] text-accent">default</span>}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-muted">
                    {c.calendars ? "Webmail with calendars and contacts" : "Webmail"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* ONE button. Where webmail is mounted is the server's answer, not a menu of
                      guesses for the user to try in turn. */}
                  <Button size="sm" variant={c.name === client ? "accent" : "default"} onClick={() => void open(c.name)}>
                    Open
                  </Button>
                  {c.name !== client && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        try {
                          await api.setWebmailDefault(c.name as never);
                          void qc.invalidateQueries({ queryKey: ["webmail-default"] });
                          notify({ kind: "success", title: `${c.title?.replace(/ (webmail|groupware)$/i, "") || c.name} is now your default` });
                        } catch (e) {
                          notify({ kind: "error", title: "Could not set the default", body: e instanceof Error ? e.message : String(e) });
                        }
                      }}
                    >
                      Make default
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </StateView>
      </Section>
    </div>
  );
}
